import { useParams, Link } from 'react-router-dom';
import { articlePageContent } from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';

const SITE_URL = 'https://cssspreadsheet.autos';

function buildArticleSchema(article: typeof fallbackContent, slug: string) {
  const faqSchema = article.faqs.length > 0
    ? {
        '@type': 'FAQPage' as const,
        mainEntity: article.faqs.map((faq) => ({
          '@type': 'Question' as const,
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer' as const,
            text: faq.answer,
          },
        })),
      }
    : null;

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Article',
      '@id': `${SITE_URL}/${slug}/#article`,
      headline: article.title,
      description: article.metaDescription,
      author: {
        '@type': 'Organization',
        name: 'CSSBuy Spreadsheet',
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'CSSBuy Spreadsheet',
        url: SITE_URL,
      },
      datePublished: article.publishedDate,
      dateModified: article.modifiedDate,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/${slug}/`,
      },
      inLanguage: 'en',
    },
  ];

  if (faqSchema) {
    graph.push(faqSchema);
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

const fallbackContent = {
  title: "CSSBuy Spreadsheet Guide",
  metaDescription: "Comprehensive CSSBuy spreadsheet guide with expert tips and resources.",
  keywords: "CSSBuy spreadsheet, CSSBuy guide, shopping spreadsheet",
  category: "Guide",
  publishedDate: "2026-07-07",
  modifiedDate: "2026-07-07",
  sections: [
    {
      heading: "Getting Started with CSSBuy Spreadsheets",
      content: "CSSBuy spreadsheets provide an organized way to manage your online shopping workflow. Whether you are tracking products, comparing prices, or managing orders, a well-structured spreadsheet is essential for efficient shopping. This guide covers everything you need to know about setting up and optimizing your CSSBuy spreadsheet for maximum productivity."
    }
  ],
  faqs: [] as { question: string; answer: string }[],
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? articlePageContent[slug as keyof typeof articlePageContent] : null;
  const article = content || fallbackContent;
  const articleSchema = buildArticleSchema(article, slug || '');

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <header className="bg-background-950 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-foreground-400">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-primary-400">{article.category}</span>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-white/70 truncate max-w-[200px] inline-block">
                  {article.title}
                </span>
              </li>
            </ol>
          </nav>

          <div className="mb-4">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium mb-4">
              {article.category}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-400">
            <span>
              <i className="ri-calendar-line mr-1"></i>
              Published: {article.publishedDate}
            </span>
            <span>
              <i className="ri-time-line mr-1"></i>
              Updated: {article.modifiedDate}
            </span>
          </div>
        </div>
      </header>

      <article className="bg-background-50">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-lg p-6 mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Table of Contents
            </h2>
            <ol className="space-y-2">
              {article.sections.map((section, idx) => (
                <li key={idx}>
                  <a
                    href={`#section-${idx}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-2"
                  >
                    <span className="text-foreground-300 text-xs">{idx + 1}.</span>
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Article Content */}
          {article.sections.map((section, idx) => (
            <section key={idx} id={`section-${idx}`} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                {section.heading}
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed">
                {section.content}
              </p>
            </section>
          ))}

          {/* FAQ */}
          {article.faqs.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-5">
                {article.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-5">
                    <h3 className="text-foreground-900 font-semibold text-base mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Internal linking - back to home */}
          <div className="mt-14 pt-10 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to CSSBuy Spreadsheet Home
            </Link>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/cssbuy-spreadsheet-complete-guide/"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                Complete Guide
              </Link>
              <Link
                to="/cssbuy-spreadsheet-template-guide/"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                Template Guide
              </Link>
              <Link
                to="/cssbuy-spreadsheet-shopping-guide/"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                Shopping Guide
              </Link>
              <Link
                to="/cssbuy-spreadsheet-vs-competitors/"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                vs Competitors
              </Link>
              <Link
                to="/cssbuy-spreadsheet-safety/"
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                Safety Guide
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
}